import React, { useEffect } from 'react'

import { useModalContext, useBanner } from '@sketch/components'

import RestoreProject from '../../modals/RestoreProject'

import { ProjectFragment, useGetWorkspaceQuery } from '@sketch/gql-types'

import { BannerButton } from '../../components/ProjectShares/ProjectShares.styles'
import { useFlag, useFolderTerm } from '@sketch/modules-common'
import { MoveStorageItemModal } from '../../../storageItems'

interface UseProjectBannerProps {
  project?: Pick<
    ProjectFragment,
    | 'deletedAt'
    | 'archivedAt'
    | '__typename'
    | 'identifier'
    | 'name'
    | 'parentProjectIdentifier'
  >
  workspaceId: string
}

export const useProjectBanner = ({
  project,
  workspaceId,
}: UseProjectBannerProps) => {
  const { showBanner } = useBanner()
  const { showModal } = useModalContext()

  const { data } = useGetWorkspaceQuery({
    variables: { identifier: workspaceId },
    fetchPolicy: 'cache-only',
  })

  const isGuest = data?.workspace?.userRole === 'GUEST'

  const folderTerm = useFolderTerm()
  const isNestedProjectsOn = useFlag('nested-projects')

  useEffect(() => {
    if (project?.deletedAt) {
      const { dismissBanner } = showBanner({
        type: 'information',
        message: (
          <>
            The <b>“{project.name}”</b> {folderTerm.lower} was deleted.{' '}
            {!isGuest && (
              <>
                <BannerButton
                  onClick={() => {
                    isNestedProjectsOn
                      ? showModal(MoveStorageItemModal, {
                          item: project,
                          workspaceIdentifier: workspaceId,
                          parentProjectIdentifier:
                            project.parentProjectIdentifier,
                          originType: 'stashed',
                        })
                      : showModal(RestoreProject, { project, workspaceId })
                  }}
                >
                  Restore the {folderTerm.lower}
                </BannerButton>
                , to edit its documents.
              </>
            )}
          </>
        ),
      })

      return dismissBanner
    }

    if (project?.archivedAt) {
      const { dismissBanner } = showBanner({
        type: 'information',
        message: (
          <>
            The <b>“{project.name}”</b> {folderTerm.lower} is archived.{' '}
          </>
        ),
      })

      return dismissBanner
    }
  }, [
    showModal,
    showBanner,
    project,
    workspaceId,
    folderTerm.lower,
    isGuest,
    isNestedProjectsOn,
  ])
}
